<template>
  <BaseDialog max-width="900" persistent :value="true">
    <template #modal>
      <BaseCosmoCard>
        <template #title>
          <div class="modal_header">
            <BaseTitle v-text="$t('nexus.modal.title')" />
            <BaseButtonIcon
              class="close_icon"
              icon="$mdiClose"
              :disabled="isSubmiting"
              @click="reset()"
            />
          </div>
        </template>
        <div class="modal_content">
          <div class="checkbox_item">
            <input
              id="nexusFolderCreationEnabled"
              v-model="form.nexusFolderCreationEnabled"
              type="checkbox"
              name="nexusFolderCreationEnabled"
            />
            <label
              for="nexusFolderCreationEnabled"
              v-text="$t('nexus.modal.enable_nexus_label')"
            />
          </div>
          <div class="form_group">
            <div
              class="input_label"
              v-text="$t('nexus.modal.action_label') + '*'"
            />
            <BaseTextField
              v-model="form.nexusFolderCreateButtonLabel"
              :placeholder="$t('nexus.modal.action_label') + '*'"
            />
          </div>
          <div class="form_group">
            <div
              class="input_label"
              v-text="$t('nexus.modal.folder_label') + '*'"
            />
            <BaseTextField
              v-model="form.nexusFolderNameInputLabel"
              :placeholder="$t('nexus.modal.folder_label') + '*'"
            />
          </div>
          <div class="form_group">
            <div
              class="input_label"
              v-text="$t('utils.assignedCollaborator') + '*'"
            />
            <base-autocomplete
              v-model="form.nexusFolderAssignedCollaborator"
              :loading="collaboratorFilter.loading"
              :items="collaboratorFilter.items"
              :filter="() => true"
              :search-input.sync="collaboratorFilter.email"
              item-text="user.email"
              item-value="user.id"
              solo
              :placeholder="`${$tc('organization.collaborator', 2)} `"
            >
              <template #append-item>
                <div v-intersect="loadMoreCollaborators" />
              </template>
            </base-autocomplete>
          </div>
        </div>
        <div class="modal_footer">
          <BaseButton
            :disabled="isSubmiting"
            color="grey"
            type="secondary"
            @click="reset()"
          >
            {{ $t("utils.cancel") }}
          </BaseButton>
          <BaseButton
            :loading="isSubmiting"
            color="primary"
            type="primary"
            @click="submit()"
          >
            {{ $t("utils.validate") }}
          </BaseButton>
        </div>
      </BaseCosmoCard>
    </template>
  </BaseDialog>
</template>
<script>
import OrganizationAPI from "@/core/Services/organization.api";
import { patchOrganizationWorkflow } from "@/modules/Admin/Services/workflow.service";
export default {
  name: "WorkflowNexusConfigurationDialog",
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    hasEnoxa: {
      type: Boolean,
    },
    workflow: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isSubmiting: false,
      form: {
        nexusFolderCreationEnabled: false,
        nexusFolderCreateButtonLabel: "",
        nexusFolderNameInputLabel: "",
        nexusFolderAssignedCollaborator: "",
      },
      collaboratorFilter: {
        loading: false,
        page: 1,
        items: [],
        pageSize: 10,
        email: undefined,
        searchTimeoutRef: undefined,
      },
    };
  },

  computed: {
    folderActionError() {
      if (!this.$v.form.nexusFolderCreateButtonLabel.$dirty) return "";
      if (!this.$v.form.nexusFolderCreateButtonLabel.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
    folderNameError() {
      if (!this.$v.form.nexusFolderNameInputLabel.$dirty) return "";
      if (!this.$v.form.nexusFolderNameInputLabel.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
  },
  watch: {
    "collaboratorFilter.email"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterCollaborators();
    },
  },
  created() {
    this.initializeForm();
    this.fetchCollaborators();
  },
  methods: {
    reset() {
      if (this.isSubmiting) return;
      this.$emit("close");
    },
    initializeForm() {
      this.form.nexusFolderCreationEnabled =
        this.workflow.nexusFolderCreationEnabled;
      this.form.nexusFolderCreateButtonLabel =
        this.workflow.nexusFolderCreateButtonLabel;
      this.form.nexusFolderNameInputLabel =
        this.workflow.nexusFolderNameInputLabel;
      if (this.workflow.nexusFolderAssignedCollaborator) {
        this.form.nexusFolderAssignedCollaborator =
          this.workflow.nexusFolderAssignedCollaborator.id;
        this.collaboratorFilter.email =
          this.workflow.nexusFolderAssignedCollaborator.email;
      }
    },
    async fetchCollaborators() {
      if (
        this.collaboratorFilter.loading &&
        !this.collaboratorFilter.page &&
        !this.organizationId
      )
        return;
      this.collaboratorFilter.loading = true;
      const res = await OrganizationAPI.getOrganizationCollaborators(
        this.organizationId,
        this.collaboratorFilter.page,
        this.collaboratorFilter.email
      );
      this.collaboratorFilter = {
        ...this.collaboratorFilter,
        items: [...res.data.results],
        next: res.data.next,
      };
      this.collaboratorFilter.loading = false;
    },
    filterCollaborators() {
      this.collaboratorFilter.page = 1;
      if (this.collaboratorFilter.searchTimeoutRef)
        clearTimeout(this.collaboratorFilter.searchTimeoutRef);
      this.collaboratorFilter.searchTimeoutRef = setTimeout(async () => {
        this.fetchCollaborators();
      }, 200);
    },
    async loadMoreCollaborators(_, __, isIntersecting) {
      if (
        !isIntersecting ||
        this.collaboratorFilter.loading ||
        !this.collaboratorFilter.next
      )
        return;
      this.collaboratorFilter.page = this.collaboratorFilter.next;
      this.collaboratorFilter.loading = true;
      try {
        const res = await OrganizationAPI.getOrganizationCollaborators(
          this.organizationId,
          this.collaboratorFilter.page,
          this.collaboratorFilter.email
        );
        this.collaboratorFilter = {
          items: [...this.collaboratorFilter.items, ...res.data.results],
          next: res.data.next,
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.questionsFilesFilter.loading = false;
    },
    async submit() {
      this.isSubmiting = true;
      try {
        const res = await patchOrganizationWorkflow(
          this.organizationId,
          this.workflow.id,
          {
            nexus_folder_creation_enabled: this.form.nexusFolderCreationEnabled,
            nexus_folder_name_input_label: this.form.nexusFolderNameInputLabel
              ? this.form.nexusFolderNameInputLabel
              : "Nom",
            nexus_folder_create_button_label: this.form
              .nexusFolderCreateButtonLabel
              ? this.form.nexusFolderCreateButtonLabel
              : this.workflow.name,
            nexus_folder_assigned_collaborator:
              this.form.nexusFolderAssignedCollaborator,
          }
        );
        this.$emit("workflowUpdated", {
          ...this.workflow,
          ...res.data,
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t(
            "workflow.snackbar.success.workflowModifiedSuccessfully"
          ),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("workflow.snackbar.success.errorModifyingWorkflow"),
          type: "ERROR",
        });
      }
      this.isSubmiting = false;
      this.reset();
    },
  },
};
</script>
<style lang="scss">
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: 8px;
  flex-wrap: wrap;
  .modal_title {
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #353542;
  }
  .close_icon {
    margin-left: auto;
  }
}
.modal_content {
  padding-top: 24px;
  .checkbox_item {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    label {
      color: #353542;
      font-family: "Inter";
      font-size: 14px;
      line-height: 18px;
      font-style: normal;
      font-weight: 400;
    }
    input {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }
  }
  .form_group {
    .input_label {
      font-size: 14px;
      line-height: 16px;
      margin-bottom: 6px;
      color: #353542;
    }
  }
}
.modal_footer {
  display: flex;
  justify-content: flex-end;
  align-content: center;
  padding: 8px 0;
  gap: 16px;
}
</style>
